(function () {
  function getVariantFromOptions(variants, optionSelects) {
    if (!Array.isArray(variants) || !optionSelects?.length) {
      return null;
    }

    // Sort option selects by their index so options map correctly
    const orderedSelects = optionSelects.slice().sort((selectA, selectB) => {
      const indexA = String(selectA.getAttribute("data-index") || "").replace("option", "");
      const indexB = String(selectB.getAttribute("data-index") || "").replace("option", "");
      return (parseInt(indexA || "0", 10) || 0) - (parseInt(indexB || "0", 10) || 0);
    });
    const selectedValues = orderedSelects.map((select) => select.value);

    // Find the variant whose options array matches all selected values
    for (const variant of variants) {
      if (!variant) {
        continue;
      }
      const isMatch = selectedValues.every((selectedValue, optionIndex) => {
        // Skip unset option groups (products with fewer than 3 options)
        if (!selectedValue) {
          return true;
        }
        return variant.options[optionIndex] === selectedValue;
      });
      if (isMatch) {
        return variant;
      }
    }
    return null;
  }

  function normalizeVariants(variants) {
    return variants.map((variant) => {
      if (!variant) {
        return variant;
      }

      // Old Shopify JSON uses "option1/2/3" not "options" array
      // https://shopify.dev/docs/api/liquid/objects/variant#variant-option1
      if (Array.isArray(variant.options) && variant.options.length > 0) {
        return variant;
      }
      const options = [variant.option1, variant.option2, variant.option3].filter(
        (value) => value !== null && value !== undefined,
      );
      return { ...variant, options };
    });
  }

  function readVariantsJson(container) {
    if (!container) {
      return null;
    }

    // Read the variants from the JSON data
    const jsonScripts = container.querySelectorAll('script[type="application/json"]');

    // Find the variant JSON data
    for (const script of jsonScripts) {
      const isVariantJson = script.hasAttribute("data-variant-json");
      const isProductJson = [...script.attributes].some((attribute) =>
        attribute.name.startsWith("data-product-json"),
      );

      // Skip scripts that do not contain variant data
      if (!isVariantJson && !isProductJson) {
        continue;
      }

      try {
        const parsed = JSON.parse(script.textContent);
        if (Array.isArray(parsed)) {
          return normalizeVariants(parsed);
        }
      } catch (error) {
        // Try the next script if this one is not valid JSON
        continue;
      }
    }
    return null;
  }

  // Toggle the visibility of the variant elements
  function toggleVariantElements(root, variantId, selector) {
    root.querySelectorAll(selector).forEach((element) => {
      const elementVariantId = Number(element.getAttribute("data-product-id"));
      const isHidden = elementVariantId !== Number(variantId);
      element.classList.toggle("hidden", isHidden);
    });
  }

  function formatMoney(cents) {
    return window.NE.Utils.formatMoney(Math.round(Number(cents || 0)));
  }

  function resetDiscountPriceState(priceElement) {
    [
      "data-ne-qv-original-html",
      "data-ne-qv-discount-price",
      "data-ne-qv-discount-signature",
    ].forEach((attribute) => priceElement.removeAttribute(attribute));
  }

  function updateVariantPrice(root, variant) {
    root.querySelectorAll("[data-compare-price]").forEach((comparePriceElement) => {
      const priceWrapper = comparePriceElement.parentElement;
      const priceElement = priceWrapper?.querySelector("[data-price]");

      if (!priceElement) {
        return;
      }

      let price = Number(variant.price || 0);
      let comparePrice = Number(variant.compare_at_price || 0);
      const preSalePrices = priceElement.getAttribute("data-pre-sale");

      if (preSalePrices) {
        try {
          const parsedPreSalePrices = JSON.parse(preSalePrices);
          const preSalePrice = Number(parsedPreSalePrices[variant.id] || 0);

          if (preSalePrice > 0) {
            comparePrice = price;
            price = preSalePrice;
          }
        } catch (error) {}
      }

      resetDiscountPriceState(priceElement);

      priceElement.textContent = formatMoney(price);
      priceElement.dataset.price = price / 100;
      priceElement.classList.toggle("sale", comparePrice > price);
      comparePriceElement.textContent =
        comparePrice > price ? formatMoney(comparePrice) : "";

      const saleBadge = priceWrapper.querySelector(
        ".product-badge__wrapper-sale",
      );

      if (saleBadge) {
        saleBadge.innerHTML =
          comparePrice > price
            ? `<span>-${(((comparePrice - price) / comparePrice) * 100).toFixed(0)}%</span>`
            : "";
        saleBadge.parentElement?.classList.toggle(
          "hidden",
          comparePrice <= price,
        );
      }
    });

    const hasUnitPrice = Boolean(
      variant.unit_price && variant.unit_price_measurement,
    );

    root
      .querySelectorAll("[data-unit-price-container], .unit-price__price")
      .forEach((container) =>
        container.classList.toggle("unit-price--available", hasUnitPrice),
      );

    if (!hasUnitPrice) {
      return;
    }

    const measurement = variant.unit_price_measurement;
    const baseUnit =
      Number(measurement.reference_value) === 1
        ? measurement.reference_unit
        : `${measurement.reference_value}${measurement.reference_unit}`;

    root
      .querySelectorAll("[data-unit-price]")
      .forEach((element) => (element.textContent = formatMoney(variant.unit_price)));
    root
      .querySelectorAll("[data-unit-price-separator]")
      .forEach((element) => (element.textContent = "/"));
    root
      .querySelectorAll("[data-unit-base]")
      .forEach((element) => (element.textContent = baseUnit));
  }

  // Initialize the variant picker
  function initQuickViewVariantPicker(quickViewRoot, options = {}) {
    // Avoid binding listeners more than once on the same quick view instance
    if (!quickViewRoot || quickViewRoot.__quickViewVariantPickerBound) {
      return;
    }

    // Read the variants from the JSON data
    const variants = readVariantsJson(quickViewRoot);
    if (!Array.isArray(variants) || variants.length === 0) {
      return;
    }

    // Get the option selects
    const optionSelects = Array.from(
      quickViewRoot.querySelectorAll('select[name^="options["]'),
    );
    if (!optionSelects.length) {
      return;
    }

    // Mark the quick view as bound to the variant picker
    quickViewRoot.__quickViewVariantPickerBound = true;

    // Get the variant select and details links
    const variantSelect = quickViewRoot.querySelector("[data-variant-select]");
    const detailsLinks = Array.from(
      quickViewRoot.querySelectorAll(
        ".quick-view-view-product-details-link[data-product-id]",
      ),
    );

    // Apply the variant to the quick view
    function applyVariant(variant) {
      if (!variant) {
        return;
      }

      // Keep the hidden variant select in sync with the visible picker
      if (variantSelect) {
        const matchingOption = variantSelect.querySelector(`option[value="${variant.id}"]`);
        if (matchingOption) {
          matchingOption.selected = true;
          variantSelect.value = String(variant.id);
        }
      }

      // Show the packshot for the selected variant and hide the others
      toggleVariantElements(
        quickViewRoot,
        variant.id,
        ".quickview-image[data-product-id]",
      );

      // Show the product-details link for the selected variant only
      detailsLinks.forEach((link) => {
        const linkVariantId = Number(link.getAttribute("data-product-id"));
        link.classList.toggle("hidden", linkVariantId !== Number(variant.id));
      });

      updateVariantPrice(quickViewRoot, variant);

      // Run caller-specific updates (e.g. BYOB price and add-to-bundle button)
      if (typeof options.onVariantApply === "function") {
        options.onVariantApply(variant, quickViewRoot);
      }

      // Keep quick-view updates isolated from the PDP variant/gallery handlers.
      document.dispatchEvent(
        new CustomEvent("quick-view:variant-change", {
          detail: { variant, root: quickViewRoot },
        }),
      );
    }

    function recomputeVariant() {
      const selectedVariant = getVariantFromOptions(variants, optionSelects);
      applyVariant(selectedVariant);
    }

    // Listen for native select changes
    optionSelects.forEach((select) => {
      if (select.__quickViewVariantPickerSelectBound) {
        return;
      }
      select.__quickViewVariantPickerSelectBound = true;
      select.addEventListener("change", recomputeVariant);
    });

    // Listen for button clicks and sync them back to the hidden select
    quickViewRoot.querySelectorAll("[data-option-buttons]").forEach((buttonGroup) => {
      const select = buttonGroup.querySelector("select");
      const buttons = buttonGroup.querySelectorAll("[data-button]");

      if (!select || !buttons.length || buttonGroup.__quickViewVariantPickerGroupBound) {
        return;
      }

      buttonGroup.__quickViewVariantPickerGroupBound = true;

      buttons.forEach((button) => {
        button.addEventListener("click", (event) => {
          event.preventDefault();

          const optionHandle = button.getAttribute("data-option-handle");
          if (!optionHandle) {
            return;
          }

          const matchingOption = select.querySelector(`[data-value-handle="${optionHandle}"]`);
          if (!matchingOption) {
            return;
          }

          matchingOption.selected = true;
          select.value = matchingOption.value;

          // Update selected state on the visible buttons
          buttons.forEach((groupButton) =>
            groupButton.classList.toggle(
              "selected",
              groupButton.getAttribute("data-option-handle") === optionHandle,
            ),
          );

          // Trigger the same change flow as a native select change
          select.dispatchEvent(new Event("change", { bubbles: true }));
        });
      });
    });

    // Sync UI to the initially selected variant on load
    recomputeVariant();
  }

  window.initQuickViewVariantPicker = initQuickViewVariantPicker;
})();
